import React, { useState } from 'react';
import CalcShell from '../CalcShell';
import { CNetRow, CNetActions, CNetResult, G } from './_cnet';

// Unit → meters
const LEN = { meters: 1, centimeters: 0.01, feet: 0.3048, inches: 0.0254, yards: 0.9144 };
const M3_TO = { ft3: 35.3146667, yd3: 1.30795062 };
const KG_PER_M3 = 2130; // pre-mixed concrete density
const LB_PER_FT3 = 133;

function results(volM3) {
  const ft3 = volM3 * M3_TO.ft3;
  const yd3 = volM3 * M3_TO.yd3;
  const kg = volM3 * KG_PER_M3;
  const lbs = kg * 2.20462;
  const bag60 = lbs / 60;
  const bag80 = lbs / 80;
  return { ft3, yd3, m3: volM3, kg, lbs, bag60, bag80 };
}

function ResultBox({ r, testId }) {
  return (
    <CNetResult>
      <div className="text-slate-900 leading-8" data-testid={`${testId}-result`}>
        Volume: <b className="text-[#4c7b25]">{G(r.ft3, 2)}</b> cubic feet<br/>
        <span className="ml-14">or <b className="text-[#4c7b25]">{G(r.yd3, 2)}</b> cubic yards</span><br/>
        <span className="ml-14">or <b className="text-[#4c7b25]">{G(r.m3, 2)}</b> cubic meters</span>
      </div>
      <div className="mt-3 text-slate-800">If using pre-mixed concrete with density of 2,130 kg/m<sup>3</sup> or 133 lbs/ft<sup>3</sup>*:</div>
      <table className="data-table mt-2" data-testid={`${testId}-table`}>
        <tbody>
          <tr><td className="w-56">Weight needed</td><td><b className="text-[#4c7b25]">{G(r.lbs, 2)}</b> lbs or <b className="text-[#4c7b25]">{G(r.kg, 2)}</b> kg</td></tr>
          <tr><td>Using 60-lb bags</td><td><b className="text-[#4c7b25]">{G(r.bag60, 2)}</b> bags</td></tr>
          <tr><td>Using 80-lb bags</td><td><b className="text-[#4c7b25]">{G(r.bag80, 2)}</b> bags</td></tr>
        </tbody>
      </table>
      <div className="text-xs text-slate-500 mt-2">* Different types of concrete can have very different densities.</div>
    </CNetResult>
  );
}

function UnitInput({ value, onChange, unit, onUnit }) {
  return (<><input className="cnet-input" type="number" value={value} onChange={e => onChange(e.target.value)} />
    <select className="cnet-select" value={unit} onChange={e => onUnit(e.target.value)}>{Object.keys(LEN).map(k => <option key={k}>{k}</option>)}</select></>);
}

/* --- Sub 1: Slab / Wall --- */
function SlabSection() {
  const [l, setL] = useState(5), [lU, setLU] = useState('meters');
  const [w, setW] = useState(2.5), [wU, setWU] = useState('meters');
  const [h, setH] = useState(5), [hU, setHU] = useState('centimeters');
  const [q, setQ] = useState(1);
  const [r, setR] = useState(null);
  const calc = () => setR(results(Number(l)*LEN[lU] * Number(w)*LEN[wU] * Number(h)*LEN[hU] * Number(q)));
  return (
    <>
      <h3 className="cnet-section-title">Slabs, Square Footings, or Walls</h3>
      <div className="grid lg:grid-cols-[1fr_180px] gap-4 items-start">
        <div className="cnet-panel">
          <CNetRow label="Length (l)"><UnitInput value={l} onChange={setL} unit={lU} onUnit={setLU} /></CNetRow>
          <CNetRow label="Width (w)"><UnitInput value={w} onChange={setW} unit={wU} onUnit={setWU} /></CNetRow>
          <CNetRow label="Thickness or Height (h)"><UnitInput value={h} onChange={setH} unit={hU} onUnit={setHU} /></CNetRow>
          <div className="cnet-row-2col"><div className="cnet-label">Quantity</div>
            <input className="cnet-input" type="number" value={q} onChange={e => setQ(e.target.value)} /></div>
          <CNetActions onCalc={calc} onClear={() => setR(null)} testId="concrete-slab" />
        </div>
        <svg viewBox="0 0 180 120" className="w-44 mx-auto">
          <defs>
            <marker id="cA" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto"><polygon points="0 0,6 3,0 6" fill="#2563eb"/></marker>
          </defs>
          {/* Top face */}
          <path d="M30,70 L110,70 L150,40 L70,40 Z" fill="none" stroke="#0f172a" strokeWidth="1.2"/>
          {/* Front face */}
          <path d="M30,70 L30,90 L110,90 L110,70" fill="none" stroke="#0f172a" strokeWidth="1.2"/>
          {/* Right face */}
          <path d="M110,90 L150,60 L150,40 M110,70 L150,40" fill="none" stroke="#0f172a" strokeWidth="1.2"/>
          {/* Hidden edges (dotted) */}
          <path d="M30,70 L70,40 M70,40 L70,60 L110,60 L110,90 M70,60 L30,90" stroke="#94a3b8" strokeWidth="0.7" strokeDasharray="2 2" fill="none"/>
          {/* Labels: l, w, h */}
          <text x="65" y="102" fontSize="10" fill="#2563eb">l</text>
          <text x="160" y="55" fontSize="10" fill="#2563eb">w</text>
          <text x="160" y="30" fontSize="10" fill="#2563eb">h</text>
        </svg>
      </div>
      {r && <ResultBox r={r} testId="concrete-slab" />}
    </>
  );
}

/* --- Sub 2: Column / Round footing --- */
function ColumnSection() {
  const [d, setD] = useState(2.5), [dU, setDU] = useState('meters');
  const [h, setH] = useState(6), [hU, setHU] = useState('meters');
  const [q, setQ] = useState(1);
  const [r, setR] = useState(null);
  const calc = () => {
    const dm = Number(d) * LEN[dU], hm = Number(h) * LEN[hU];
    setR(results(Math.PI * (dm / 2) ** 2 * hm * Number(q)));
  };
  return (
    <>
      <h3 className="cnet-section-title">Hole, Column, or Round Footings</h3>
      <div className="grid lg:grid-cols-[1fr_180px] gap-4 items-start">
        <div className="cnet-panel">
          <CNetRow label="Diameter (d)"><UnitInput value={d} onChange={setD} unit={dU} onUnit={setDU} /></CNetRow>
          <CNetRow label="Depth or Height (h)"><UnitInput value={h} onChange={setH} unit={hU} onUnit={setHU} /></CNetRow>
          <div className="cnet-row-2col"><div className="cnet-label">Quantity</div><input className="cnet-input" type="number" value={q} onChange={e => setQ(e.target.value)} /></div>
          <CNetActions onCalc={calc} onClear={() => setR(null)} testId="concrete-col" />
        </div>
        <svg viewBox="0 0 180 130" className="w-44 mx-auto">
          <defs>
            <marker id="cAd" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto"><polygon points="0 0,6 3,0 6" fill="#2563eb"/></marker>
            <marker id="cAd2" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto"><polygon points="6 0,0 3,6 6" fill="#2563eb"/></marker>
          </defs>
          {/* Cylinder */}
          <ellipse cx="90" cy="35" rx="50" ry="12" fill="none" stroke="#0f172a" strokeWidth="1.2"/>
          <line x1="40" y1="35" x2="40" y2="105" stroke="#0f172a"/>
          <line x1="140" y1="35" x2="140" y2="105" stroke="#0f172a"/>
          <path d="M40,105 A50,12 0 0 0 140,105" fill="none" stroke="#0f172a" strokeWidth="1.2"/>
          <path d="M40,105 A50,12 0 0 1 140,105" fill="none" stroke="#94a3b8" strokeDasharray="2 2"/>
          {/* Diameter arrow (top) */}
          <line x1="40" y1="20" x2="140" y2="20" stroke="#2563eb" markerStart="url(#cAd2)" markerEnd="url(#cAd)"/>
          <text x="82" y="14" fontSize="10" fill="#2563eb">d</text>
          {/* Height arrow (right) */}
          <line x1="155" y1="35" x2="155" y2="105" stroke="#2563eb" markerStart="url(#cAd2)" markerEnd="url(#cAd)"/>
          <text x="160" y="72" fontSize="10" fill="#2563eb">h</text>
        </svg>
      </div>
      {r && <ResultBox r={r} testId="concrete-col" />}
    </>
  );
}

/* --- Sub 3: Circular slab / Tube --- */
function TubeSection() {
  const [d1, setD1] = useState(5), [d1U, setD1U] = useState('meters');
  const [d2, setD2] = useState(4), [d2U, setD2U] = useState('meters');
  const [h, setH] = useState(6), [hU, setHU] = useState('centimeters');
  const [q, setQ] = useState(1);
  const [r, setR] = useState(null);
  const calc = () => {
    const D = Number(d1) * LEN[d1U], d = Number(d2) * LEN[d2U], H = Number(h) * LEN[hU];
    setR(results(Math.PI * ((D / 2) ** 2 - (d / 2) ** 2) * H * Number(q)));
  };
  return (
    <>
      <h3 className="cnet-section-title">Circular Slab or Tube</h3>
      <div className="grid lg:grid-cols-[1fr_180px] gap-4 items-start">
        <div className="cnet-panel">
          <CNetRow label="Outer Diameter (d₁)"><UnitInput value={d1} onChange={setD1} unit={d1U} onUnit={setD1U} /></CNetRow>
          <CNetRow label="Inner Diameter (d₂)"><UnitInput value={d2} onChange={setD2} unit={d2U} onUnit={setD2U} /></CNetRow>
          <CNetRow label="Length or Height (h)"><UnitInput value={h} onChange={setH} unit={hU} onUnit={setHU} /></CNetRow>
          <div className="cnet-row-2col"><div className="cnet-label">Quantity</div><input className="cnet-input" type="number" value={q} onChange={e => setQ(e.target.value)} /></div>
          <CNetActions onCalc={calc} onClear={() => setR(null)} testId="concrete-tube" />
        </div>
        <svg viewBox="0 0 180 130" className="w-44 mx-auto">
          <defs>
            <marker id="tA" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto"><polygon points="0 0,6 3,0 6" fill="#2563eb"/></marker>
            <marker id="tA2" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto"><polygon points="6 0,0 3,6 6" fill="#2563eb"/></marker>
          </defs>
          {/* Outer cylinder */}
          <ellipse cx="90" cy="35" rx="55" ry="14" fill="none" stroke="#0f172a"/>
          <ellipse cx="90" cy="35" rx="22" ry="6" fill="none" stroke="#0f172a"/>
          <line x1="35" y1="35" x2="35" y2="100" stroke="#0f172a"/>
          <line x1="145" y1="35" x2="145" y2="100" stroke="#0f172a"/>
          <path d="M35,100 A55,14 0 0 0 145,100" stroke="#0f172a" fill="none"/>
          <path d="M35,100 A55,14 0 0 1 145,100" stroke="#94a3b8" fill="none" strokeDasharray="2 2"/>
          <line x1="68" y1="98" x2="112" y2="98" stroke="#94a3b8" strokeDasharray="2 2"/>
          {/* d2 arrow at top */}
          <line x1="68" y1="18" x2="112" y2="18" stroke="#2563eb" markerStart="url(#tA2)" markerEnd="url(#tA)"/>
          <text x="83" y="12" fontSize="10" fill="#2563eb">d₂</text>
          {/* d1 arrow at bottom */}
          <line x1="35" y1="122" x2="145" y2="122" stroke="#2563eb" markerStart="url(#tA2)" markerEnd="url(#tA)"/>
          <text x="80" y="118" fontSize="10" fill="#2563eb">d₁</text>
          {/* h arrow */}
          <line x1="155" y1="35" x2="155" y2="100" stroke="#2563eb" markerStart="url(#tA2)" markerEnd="url(#tA)"/>
          <text x="160" y="70" fontSize="10" fill="#2563eb">h</text>
        </svg>
      </div>
      {r && <ResultBox r={r} testId="concrete-tube" />}
    </>
  );
}

/* --- Sub 4: Curb and Gutter --- */
function CurbSection() {
  const [cd, setCd] = useState(4), [cdU, setCdU] = useState('centimeters');
  const [gw, setGw] = useState(10), [gwU, setGwU] = useState('centimeters');
  const [ch, setCh] = useState(4), [chU, setChU] = useState('centimeters');
  const [ft, setFt] = useState(5), [ftU, setFtU] = useState('centimeters');
  const [l, setL] = useState(10), [lU, setLU] = useState('meters');
  const [q, setQ] = useState(1);
  const [r, setR] = useState(null);
  const calc = () => {
    // Cross-section area (m²) = curbDepth·curbHeight + gutterWidth·flagThickness
    const CD = Number(cd)*LEN[cdU], GW = Number(gw)*LEN[gwU], CH = Number(ch)*LEN[chU], FT = Number(ft)*LEN[ftU], L = Number(l)*LEN[lU];
    const area = CD * CH + GW * FT;
    setR(results(area * L * Number(q)));
  };
  return (
    <>
      <h3 className="cnet-section-title">Curb and Gutter Barrier</h3>
      <div className="grid lg:grid-cols-[1fr_320px] gap-4 items-start">
        <div className="cnet-panel">
          <CNetRow label="Curb Depth"><UnitInput value={cd} onChange={setCd} unit={cdU} onUnit={setCdU} /></CNetRow>
          <CNetRow label="Gutter Width"><UnitInput value={gw} onChange={setGw} unit={gwU} onUnit={setGwU} /></CNetRow>
          <CNetRow label="Curb Height"><UnitInput value={ch} onChange={setCh} unit={chU} onUnit={setChU} /></CNetRow>
          <CNetRow label="Flag Thickness"><UnitInput value={ft} onChange={setFt} unit={ftU} onUnit={setFtU} /></CNetRow>
          <CNetRow label="Length"><UnitInput value={l} onChange={setL} unit={lU} onUnit={setLU} /></CNetRow>
          <div className="cnet-row-2col"><div className="cnet-label">Quantity</div><input className="cnet-input" type="number" value={q} onChange={e => setQ(e.target.value)} /></div>
          <CNetActions onCalc={calc} onClear={() => setR(null)} testId="concrete-curb" />
        </div>
        <div className="flex flex-col items-center">
          <img
            src="/diagrams/concrete-curb.png"
            alt="Curb and Gutter Barrier reference — Curb Depth, Gutter Width, Curb Height, Flag Thickness, Length"
            className="w-full object-contain"
            data-testid="concrete-curb-diagram"
          />
        </div>
      </div>
      {r && <ResultBox r={r} testId="concrete-curb" />}
    </>
  );
}

/* --- Sub 5: Stairs --- */
function StairsSection() {
  const [run, setRun] = useState(12), [runU, setRunU] = useState('centimeters');
  const [rise, setRise] = useState(6), [riseU, setRiseU] = useState('centimeters');
  const [wid, setWid] = useState(50), [widU, setWidU] = useState('centimeters');
  const [plat, setPlat] = useState(5), [platU, setPlatU] = useState('centimeters');
  const [n, setN] = useState(5);
  const [r, setR] = useState(null);
  const calc = () => {
    const RUN = Number(run)*LEN[runU], RISE = Number(rise)*LEN[riseU], W = Number(wid)*LEN[widU], PLAT = Number(plat)*LEN[platU], N = Number(n);
    // volume = W × Σ(step_i_top_area) — a triangular staircase filling
    // Simpler total: cumulative triangle staircase: (N × RUN × RISE / 2 + N × RUN × PLAT) × W? Actually calculator.net uses:
    // volume = W × (0.5 · N · (N+1) · RUN · RISE + N · RUN · PLAT)   — approximated
    const stepVol = W * (0.5 * N * (N + 1) * RUN * RISE + N * RUN * PLAT);
    setR(results(stepVol));
  };
  return (
    <>
      <h3 className="cnet-section-title">Stairs</h3>
      <div className="grid lg:grid-cols-[1fr_320px] gap-4 items-start">
        <div className="cnet-panel">
          <CNetRow label="Run"><UnitInput value={run} onChange={setRun} unit={runU} onUnit={setRunU} /></CNetRow>
          <CNetRow label="Rise"><UnitInput value={rise} onChange={setRise} unit={riseU} onUnit={setRiseU} /></CNetRow>
          <CNetRow label="Width"><UnitInput value={wid} onChange={setWid} unit={widU} onUnit={setWidU} /></CNetRow>
          <CNetRow label="Platform Depth"><UnitInput value={plat} onChange={setPlat} unit={platU} onUnit={setPlatU} /></CNetRow>
          <div className="cnet-row-2col"><div className="cnet-label">Number of Risers</div><input className="cnet-input" type="number" value={n} onChange={e => setN(e.target.value)} /></div>
          <CNetActions onCalc={calc} onClear={() => setR(null)} testId="concrete-stairs" />
        </div>
        <div className="flex flex-col items-center">
          <img
            src="/diagrams/concrete-stairs.png"
            alt="Stairs reference — Run, Rise, Width, Platform Depth, Number of Risers"
            className="w-full object-contain"
            data-testid="concrete-stairs-diagram"
          />
        </div>
      </div>
      {r && <ResultBox r={r} testId="concrete-stairs" />}
    </>
  );
}

export default function ConcreteCalc() {
  return (
    <CalcShell look="cnet" showActions={false} title="Concrete Calculator" subtitle="The Concrete Calculator estimates the volume and weight of concrete necessary to cover a given area.">
      <p className="text-sm text-slate-700 mb-4">The Concrete Calculator estimates the volume and weight of concrete necessary to cover a given area. Purchasing slightly more concrete than the estimated result can reduce the probability of having insufficient concrete.</p>
      <SlabSection />
      <ColumnSection />
      <TubeSection />
      <CurbSection />
      <StairsSection />
    </CalcShell>
  );
}
